// BottomBarChat.jsx — GlobalChatDock alinhado com ChatDMModern
// Agora controlado externamente via props `open` e `onClose`
// - Usa mesmos eventos de socket: message:new, typing, presence:update, message:read
// - Typing "digitando…" funcionando
// - Som de nova mensagem igual ao ChatDMModern (AudioContext)
// - Auto-scroll + dedupe (clientMsgId)

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "../config/axiosInstance";
import { CHAT, USERS } from "../config/apiConfig";
import { useSocketReady, useSocketRef } from "../contexts/SocketContext";

const cx = (...c) => c.filter(Boolean).join(" ");
const fmtTime = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});

const normMsg = (m) => ({
  id: m.id,
  clientMsgId: m.clientMsgId || undefined,
  text: m.text ?? m.body ?? "",
  senderId: m.senderId,
  createdAt: m.createdAt ? new Date(m.createdAt).getTime() : Date.now(),
  isRead: !!m.isRead,
  isDelivered: m.isDelivered ?? true,
});

const springSnappy = { type: "spring", stiffness: 420, damping: 32, mass: 0.8 };
const panelAnim = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: springSnappy },
  exit: { opacity: 0, y: 40, transition: { duration: 0.18 } },
};
const listStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04, delayChildren: 0.04 } },
};

const canNotify = () =>
  typeof window !== "undefined" && "Notification" in window;

const showMessageNotification = (fromUser, text) => {
  try {
    if (!canNotify()) return;
    if (Notification.permission !== "granted") return;

    const title = fromUser?.name
      ? `Nova mensagem de ${fromUser.name}`
      : "Nova mensagem";

    const body =
      (text || "").trim().slice(0, 80) || "Você recebeu uma nova mensagem.";

    const icon = fromUser?.avatar || undefined;

    new Notification(title, { body, icon });
  } catch (e) {
    console.warn("[GlobalChatDock] notification error", e);
  }
};

const itemPop = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: springSnappy },
};

/* ========================= Avatar simples ========================= */
function Avatar({ name = "?", url, size = 30, online = false }) {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {url ? (
        <img
          src={url}
          alt={name}
          className="h-full w-full rounded-full object-cover ring-1 ring-black/5"
        />
      ) : (
        <div className="h-full w-full rounded-full bg-gradient-to-br from-orange-50 to-white text-xs text-[#e45412] flex items-center justify-center font-semibold ring-1 ring-black/5">
          {initials || "?"}
        </div>
      )}
      <span
        className={cx(
          "absolute right-0 bottom-0 h-2 w-2 rounded-full ring-2 ring-white",
          online ? "bg-green-500" : "bg-gray-300"
        )}
      />
    </div>
  );
}

/* ========================= Bolha ========================= */
function Bubble({ meId, m }) {
  const mine = String(m.senderId) === String(meId);
  return (
    <motion.div
      variants={itemPop}
      initial="hidden"
      animate="show"
      className={cx("max-w-[200px] group mb-1.5", mine ? "ml-auto" : "mr-auto")}
    >
      <div
        className={cx(
          "rounded-2xl px-2.5 py-2 shadow",
          mine
            ? "bg-[#e45412] text-white shadow-[0_8px_24px_rgba(227,84,18,.35)]"
            : "bg-gray-100 text-gray-900 dark:bg-[#0d1422] dark:text-white/90 border border-transparent"
        )}
      >
        {!!m.text && (
          <div className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
            {m.text}
          </div>
        )}
        <div
          className={cx(
            "mt-1 flex justify-end items-center gap-2 text-[10px] opacity-80",
            mine ? "text-indigo-100" : "text-gray-500"
          )}
        >
          <span>{fmtTime.format(new Date(m.createdAt || Date.now()))}</span>
          {m.failed && mine && (
            <span className="text-[9px] text-red-200">falhou</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ========================= Componente principal (dock) ========================= */
/**
 * Props:
 * - `open`: boolean -> se o dock está aberto
 * - `onClose`: função chamada ao fechar
 * - `sidebarCollapsed`: pra alinhar com a largura do sidebar
 */
export default function GlobalChatDock({
  sidebarCollapsed = false,
  open = false,
  onClose,
  onUnreadChange,
}) {
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [activeUser, setActiveUser] = useState(null);
  const [me, setMe] = useState(null);

  // mapa de não lidas por usuário: { [userId]: count }
  const [unreadByUser, setUnreadByUser] = useState({});

  // guarda o título original da aba
  const baseTitleRef = useRef(
    typeof document !== "undefined" ? document.title : "Relinq"
  );

  // Audio para notificação global (dock) — bip tipo WhatsApp
  const notifyAudioCtxRef = useRef(null);
  const playNotifyPing = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;

      if (!notifyAudioCtxRef.current) {
        notifyAudioCtxRef.current = new AC();
      }

      const ctx = notifyAudioCtxRef.current;
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, now);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.25);
    } catch (e) {
      console.warn("[GlobalChatDock] notify ping error", e);
    }
  }, []);

  const totalUnread = useMemo(
    () =>
      Object.values(unreadByUser || {}).reduce((acc, v) => acc + (v || 0), 0),
    [unreadByUser]
  );

  useEffect(() => {
    if (typeof onUnreadChange === "function") {
      onUnreadChange(totalUnread);
    }
  }, [totalUnread, onUnreadChange]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${baseTitleRef.current}`;
    } else {
      document.title = baseTitleRef.current;
    }
  }, [totalUnread]);

  useEffect(() => {
    if (!open) setActiveUser(null);
  }, [open]);

  // carrega "me" + usuários
  useEffect(() => {
    (async () => {
      try {
        const meRes = await axios.get(USERS.GET_USER_CURRENT);
        const meData = meRes?.data?.data;
        setMe(meData);

        const uRes = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        const list = (
          uRes?.data?.data?.data ||
          uRes?.data?.data ||
          uRes?.data ||
          []
        ).filter((u) => String(u.id) !== String(meData?.id));
        setUsers(list);

        // bootstrap de presença igual ao ChatDMModern
        try {
          const ids = list.map((u) => u.id).join(",");
          if (ids) {
            const pres = await axios.get(`${CHAT.PRESENCE}?ids=${ids}`);
            const map = pres?.data?.data || {};
            setUsers((prev) =>
              prev.map((u) => {
                const p = map[String(u.id)];
                return p
                  ? { ...u, online: !!p.online, lastSeen: p.lastSeen || null }
                  : u;
              })
            );
          }
        } catch (e) {
          console.warn("[GlobalChatDock] presence bootstrap falhou", e);
        }
      } catch (e) {
        console.error("[GlobalChatDock] bootstrap", e);
      }
    })();
  }, []);

  // presença em tempo real
  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s) return;

    const onPresence = ({ userId, online, lastSeen }) => {
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id) === String(userId)
            ? { ...u, online: !!online, lastSeen: lastSeen || u.lastSeen }
            : u
        )
      );
      setActiveUser((prev) =>
        prev && String(prev.id) === String(userId)
          ? { ...prev, online: !!online, lastSeen: lastSeen || prev.lastSeen }
          : prev
      );
    };

    const rebootstrap = async () => {
      try {
        if (!users || users.length === 0) return;
        const ids = users.map((u) => u.id).join(",");
        if (!ids) return;
        const pres = await axios.get(`${CHAT.PRESENCE}?ids=${ids}`);
        const map = pres?.data?.data || {};
        setUsers((prev) =>
          prev.map((u) => {
            const p = map[String(u.id)];
            return p
              ? { ...u, online: !!p.online, lastSeen: p.lastSeen || null }
              : u;
          })
        );
      } catch (e) {
        console.warn("[GlobalChatDock] rebootstrap presence", e);
      }
    };

    s.on("presence:update", onPresence);
    s.on("reconnect", rebootstrap);
    s.on("connect", rebootstrap);

    return () => {
      try {
        s.off("presence:update", onPresence);
        s.off("reconnect", rebootstrap);
        s.off("connect", rebootstrap);
      } catch {}
    };
  }, [socketRef, socketReady, users]);

  // Marca não lida global
  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s || !me?.id) return;

    const onNewDockMessage = ({ message }) => {
      if (!message) return;

      const fromMe = String(message.senderId) === String(me.id);
      if (fromMe) return;

      const senderId = message.senderId;
      if (!senderId) return;

      const isSameUserOpen =
        activeUser && String(activeUser.id) === String(senderId);

      if (!open || !isSameUserOpen) {
        setUnreadByUser((prev) => ({
          ...prev,
          [senderId]: (prev?.[senderId] || 0) + 1,
        }));

        playNotifyPing();
      }
    };

    s.on("message:new", onNewDockMessage);
    return () => {
      try {
        s.off("message:new", onNewDockMessage);
      } catch {}
    };
  }, [socketRef, socketReady, me, open, activeUser, playNotifyPing]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = users;
    if (term) {
      list = users.filter(
        (u) =>
          String(u.name || "")
            .toLowerCase()
            .includes(term) ||
          String(u.email || "")
            .toLowerCase()
            .includes(term)
      );
    }
    return [...list].sort(
      (a, b) =>
        Number(!!b.online) - Number(!!a.online) || a.name.localeCompare(b.name)
    );
  }, [users, q]);

  // ESC fecha dock
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setActiveUser(null);
        onClose?.();
      }
    };
    if (!open) return;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSelectUser = (u) => {
    setActiveUser(u);
    setUnreadByUser((prev) => {
      if (!prev) return {};
      const next = { ...prev };
      delete next[u.id];
      return next;
    });
  };

  const handleCloseDock = () => {
    setActiveUser(null);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className={cx(
        "fixed bottom-0 z-[60]",
        "left-2 sm:left-4",
        sidebarCollapsed ? "lg:left-[72px]" : "lg:left-[272px]"
      )}
      style={{ minWidth: "450px", maxWidth: "450px" }}
    >
      <div className="px-0 sm:px-0 pb-2">
        <div className="pointer-events-auto flex flex-col items-end gap-2 w-full sm:w-auto">
          <AnimatePresence>
            <DockPanel
              filtered={filtered}
              q={q}
              setQ={setQ}
              loadingUsers={loadingUsers}
              activeUser={activeUser}
              setActiveUser={setActiveUser}
              onCloseDock={handleCloseDock}
              me={me}
              unreadByUser={unreadByUser}
              onUserSelected={handleSelectUser}
            />
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ========================= Painel (lista OU conversa) ========================= */
function DockPanel({
  filtered,
  q,
  setQ,
  loadingUsers,
  activeUser,
  setActiveUser,
  onCloseDock,
  me,
  unreadByUser,
  onUserSelected,
}) {
  const showList = !activeUser;
  const showConversation = !!activeUser;

  return (
    <motion.div
      key="dock-panel"
      variants={panelAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      className={cx(
        "w-full sm:w-[360px] xl:w-[400px] 2xl:w-[440px]",
        "rounded-2xl border border-gray-200/80 dark:border-white/10",
        "bg-white/95 dark:bg-[#0b0f1a]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,.35)]",
        "overflow-hidden flex flex-col"
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {showList && (
          <UsersPanel
            key="users"
            users={filtered}
            q={q}
            setQ={setQ}
            loading={loadingUsers}
            onSelect={(u) => onUserSelected?.(u)}
            onCloseDock={onCloseDock}
            unreadByUser={unreadByUser}
          />
        )}

        {showConversation && (
          <ConversationWindow
            key={`conv_${activeUser.id}`}
            user={activeUser}
            me={me}
            onClose={onCloseDock}
            onBack={() => setActiveUser(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ========================= Painel de usuários ========================= */
function UsersPanel({
  users,
  q,
  setQ,
  loading,
  onSelect,
  onCloseDock,
  unreadByUser,
}) {
  return (
    <motion.div
      layout
      variants={panelAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-[60vh] sm:h-[420px]"
    >
      <div className="px-3.5 md:px-4 py-3 border-b border-gray-200/80 dark:border-white/10 bg-gray-50 dark:bg-white/5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm md:text-base font-semibold dark:text-white">
            Mensagens
          </div>
          <button
            type="button"
            onClick={onCloseDock}
            className="px-2 py-1 text-xs rounded hover:bg-black/5 dark:hover:bg-white/10"
          >
            Fechar
          </button>
        </div>
        <div className="mt-2.5">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou e-mail"
            className="w-full rounded-xl border border-gray-200 dark:border-white/10 px-3 py-1.5 text-sm dark:bg-transparent dark:text-white focus:ring-2 focus:ring-[#e45412]/30 focus:border-[#e45412]"
          />
        </div>
      </div>

      <motion.div
        variants={listStagger}
        initial="hidden"
        animate="show"
        className="flex-1 overflow-y-auto p-2.5 md:p-3 space-y-1 bg-white dark:bg-[#050814]"
      >
        {loading && (
          <div className="text-center text-xs text-gray-500 py-4">
            Carregando…
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="text-center text-xs text-gray-500 py-6">
            Nenhum usuário
          </div>
        )}
        {users.map((u) => {
          const unreadCount = unreadByUser?.[u.id] || 0;
          return (
            <motion.button
              key={u.id}
              variants={itemPop}
              type="button"
              onClick={() => onSelect?.(u)}
              className="w-full text-left px-3 py-2.5 rounded-xl border border-transparent hover:border-gray-200 dark:hover:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 flex items-center gap-3 md:gap-3.5"
            >
              <Avatar
                name={u.name}
                url={u.avatar}
                online={!!u.online}
                size={30}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium dark:text-white truncate">
                    {u.name}
                  </div>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] px-1.5 py-0.5 ml-1 animate-pulse">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-white/60 truncate">
                  {u.email}
                </div>
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </motion.div>
  );
}

/* ========================= Janela de conversa ========================= */
function ConversationWindow({ user, me, onClose, onBack }) {
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();

  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [input, setInput] = useState("");
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);

  const bottomRef = useRef(null);

  const seenIdsRef = useRef(new Set());
  const pendingByClientIdRef = useRef(new Map());
  const meRef = useRef(me);
  useEffect(() => {
    meRef.current = me;
  }, [me]);

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!canNotify()) return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const audioCtxRef = useRef(null);
  const playPing = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.2);
    } catch {}
  }, []);

  const smoothBottom = useCallback(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  const instantBottom = useCallback(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ block: "end" });
  }, []);

  // garantir conversa (DM_ENSURE)
  useEffect(() => {
    (async () => {
      try {
        if (!user?.id) return;
        setMessages([]);
        setHasMore(false);
        seenIdsRef.current = new Set();
        pendingByClientIdRef.current = new Map();

        const res = await axios.post(CHAT.DM_ENSURE, {
          userId: Number(user.id),
        });
        const conv = res?.data?.data?.conversation || res?.data?.data;
        if (!conv?.id) return;
        setConvId(conv.id);

        const s = socketRef?.current;
        if (socketReady && s) {
          s.emit("leaveAll");
          s.emit("dm:join", { conversationId: conv.id });
          s.emit("join", { room: `dm:${conv.id}` });
        }

        await loadMessages(conv.id);
        setTimeout(() => {
          instantBottom();
        }, 0);
      } catch (e) {
        console.error("[GlobalChatDock] ensure conv", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, socketReady]);

  const loadMessages = async (conversationId, beforeId) => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const res = await axios.get(CHAT.DM_MESSAGES(conversationId), {
        params: { beforeId, limit: 30 },
      });
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const n = data.map(normMsg).sort((a, b) => a.createdAt - b.createdAt);

      n.forEach((m) => m.id && seenIdsRef.current.add(String(m.id)));

      setMessages((prev) => (beforeId ? [...n, ...prev] : [...prev, ...n]));
      setHasMore(res?.data?.hasMore ?? data.length === 30);

      if (!beforeId && n.length) {
        await markRead(conversationId, n[n.length - 1].id);
      }
    } catch (e) {
      console.error("[GlobalChatDock] load messages", e);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (conversationId, lastReadMessageId) => {
    try {
      await axios.post(CHAT.DM_READ(conversationId), { lastReadMessageId });
      setMessages((prev) =>
        prev.map((m) =>
          m.senderId !== meRef.current?.id ? { ...m, isRead: true } : m
        )
      );
    } catch {}
  };

  // eventos de socket
  useEffect(() => {
    const s = socketRef?.current;
    if (!socketReady || !s || !convId) return;

    const onNew = ({ conversationId, message }) => {
      if (String(conversationId) !== String(convId)) return;

      const mid = String(message.id || "");
      const cmid = String(message.clientMsgId || "");
      const fromMe =
        String(message.senderId) === String(meRef.current?.id ?? "");

      // merge otimista
      if (cmid && pendingByClientIdRef.current.has(cmid)) {
        const tempId = pendingByClientIdRef.current.get(cmid);
        pendingByClientIdRef.current.delete(cmid);
        seenIdsRef.current.add(mid);

        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? {
                  ...normMsg(message),
                  senderId: message.senderId ?? meRef.current?.id,
                  isDelivered: true,
                }
              : m
          )
        );

        setTimeout(() => {
          smoothBottom();
        }, 0);
      } else {
        if (mid && seenIdsRef.current.has(mid)) return;
        seenIdsRef.current.add(mid);

        setMessages((prev) => [...prev, normMsg(message)]);

        setTimeout(() => {
          smoothBottom();
        }, 0);
      }

      if (!fromMe) {
        playPing();
        const text = message.text ?? message.body ?? "";
        showMessageNotification(userRef.current, text);
      }

      if (message.id) markRead(conversationId, message.id);
    };

    const onTyping = ({ conversationId, userId, isTyping }) => {
      if (String(conversationId) !== String(convId)) return;
      if (String(userId) === String(meRef.current?.id)) return;
      setIsPartnerTyping(!!isTyping);
      if (isTyping) {
        setTimeout(() => setIsPartnerTyping(false), 2500);
      }
    };

    const onRead = ({ conversationId, readerId, lastReadMessageId }) => {
      if (String(conversationId) !== String(convId)) return;
      if (String(readerId) === String(meRef.current?.id)) return;
      setMessages((prev) =>
        prev.map((m) =>
          String(m.senderId) === String(meRef.current?.id) &&
          String(m.id) <= String(lastReadMessageId)
            ? { ...m, isRead: true }
            : m
        )
      );
    };

    const rejoin = () => {
      s.emit("leaveAll");
      s.emit("dm:join", { conversationId: convId });
      s.emit("join", { room: `dm:${convId}` });
    };

    s.on("message:new", onNew);
    s.on("typing", onTyping);
    s.on("message:read", onRead);
    s.on("connect", rejoin);
    s.on("reconnect", rejoin);

    return () => {
      try {
        s.off("message:new", onNew);
        s.off("typing", onTyping);
        s.off("message:read", onRead);
        s.off("connect", rejoin);
        s.off("reconnect", rejoin);
      } catch {}
    };
  }, [socketRef, socketReady, convId, playPing, smoothBottom]);

  const typingTimeoutRef = useRef(null);
  const emitTyping = useCallback(
    (isTyping) => {
      const s = socketRef?.current;
      if (!socketReady || !s || !convId) return;
      s.emit("typing", { conversationId: convId, isTyping });
    },
    [socketRef, socketReady, convId]
  );

  const handleInputChange = (e) => {
    setInput(e.target.value);
    emitTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => emitTyping(false), 800);
  };

  const onSend = async () => {
    const text = input.trim();
    if (!text || !convId) return;

    const clientMsgId = `c_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    const tempId = `t_${Date.now()}`;

    pendingByClientIdRef.current.set(clientMsgId, tempId);

    const optimistic = normMsg({
      id: tempId,
      clientMsgId,
      text,
      senderId: meRef.current?.id,
      createdAt: Date.now(),
      isDelivered: false,
      isRead: false,
    });

    setMessages((prev) => [...prev, optimistic]);
    setInput("");

    setTimeout(() => {
      instantBottom();
    }, 0);

    try {
      const res = await axios.post(CHAT.DM_SEND, {
        text,
        conversationId: convId,
        clientMsgId,
      });
      const real = normMsg(res?.data?.data || {});
      if (real.id) seenIdsRef.current.add(String(real.id));
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...real,
                senderId: real.senderId ?? meRef.current?.id,
                isDelivered: true,
              }
            : m
        )
      );
      setTimeout(() => {
        smoothBottom();
      }, 0);
    } catch (e) {
      console.error("[GlobalChatDock] send failed", e);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, failed: true, isDelivered: false } : m
        )
      );
      pendingByClientIdRef.current.delete(clientMsgId);
    }
  };

  return (
    <motion.div
      layout
      variants={panelAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-[50vh] sm:h-[420px]"
    >
      {/* Header */}
      <div className="h-12 px-3.5 md:px-4 flex items-center justify-between bg-gray-100 dark:bg-white/5 border-b border-gray-200/80 dark:border-white/10">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="mr-1.5 p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10"
            title="Voltar para a lista"
          >
            ←
          </button>

          <Avatar
            name={user?.name}
            url={user?.avatar}
            size={30}
            online={!!user?.online}
          />
          <div className="truncate">
            <div className="text-sm md:text-[15px] font-semibold dark:text-white truncate">
              {user?.name || "Usuário"}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-white/60 truncate">
              {isPartnerTyping
                ? "digitando…"
                : user?.online
                ? "online"
                : "offline"}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg:white/10"
          title="Fechar"
        >
          ×
        </button>
      </div>

      {/* Mensagens */}
      <div
        className="flex-1 overflow-y-auto pl-2 pr-2 py-2.5 bg-white dark:bg-[#0b0f1a]"
        style={{ maxHeight: "45vh", minHeight: "40vh" }}
      >
        {hasMore && (
          <div className="text-center">
            <button
              onClick={() => loadMessages(convId, messages[0]?.id)}
              className="text-[12px] text-gray-600 dark:text-white/70 hover:underline"
            >
              Carregar anteriores
            </button>
          </div>
        )}
        <div className="mt-2 space-y-1.5 pr-1">
          {loading && messages.length === 0 && (
            <div className="text-xs text-gray-500 dark:text:white/60 text-center py-4">
              Carregando mensagens…
            </div>
          )}
          {messages.map((m) => (
            <Bubble key={m.id} meId={me?.id} m={m} />
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-gray-200/80 dark:border:white/10 p-2.5 md:p-3 bg:white/80 dark:bg-[#0b0f1a]/80 backdrop-blur">
        <div className="flex items-end gap-2">
          <textarea
            rows={1}
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (e.altKey) return;
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Escreva uma mensagem…"
            className="flex-1 resize-none rounded-xl border border-gray-200 dark:border:white/10 focus:ring-2 focus:ring-[#e45412]/30 focus:border-[#e45412] px-3 py-2 text-sm dark:text-white bg-white/80 dark:bg-transparent"
          />
          <button
            type="button"
            onClick={onSend}
            className="px-3 md:px-3.5 py-2 rounded-xl bg-[#e45412] text-white text-sm font-medium hover:brightness-110 shadow-[0_6px_18px_rgba(227,84,18,.35)]"
          >
            Enviar
          </button>
        </div>
      </div>
    </motion.div>
  );
}
